import { useState } from 'react';
import { COLOR_PRESETS, applyColorPreset } from '@/hooks/useStoreSettings';
import { GRADIENT_PRESETS, PAGE_BG_PRESETS } from './designConstants';
import { DesignSettings, SectionSaveState } from './designTypes';
import { SaveButton } from './DesignShared';

interface Props {
  design: DesignSettings;
  updateDesign: (patch: Partial<DesignSettings>) => void;
  colorSaveState: SectionSaveState;
  onSaveColors: () => void;
  selectedColor: typeof COLOR_PRESETS[0];
}

export default function ColorsSection({ design, updateDesign, colorSaveState, onSaveColors, selectedColor }: Props) {
  const [customBg, setCustomBg] = useState(design.pageBgCustomColor || '#0a0a0a');

  const selectedBg = PAGE_BG_PRESETS.find(b => b.id === design.pageBgId) ?? PAGE_BG_PRESETS[0];
  const activeBgColor = design.pageBgId === 'custom' ? customBg : selectedBg.color;
  const isLightBg = ['off-white', 'warm-white', 'light-gray', 'cream'].includes(design.pageBgId);

  return (
    <div className="flex flex-col gap-5">

      {/* ── Primary Color ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center rounded-lg" style={{ background: selectedColor.primary }}>
            <i className="ri-palette-line text-[10px]" style={{ color: selectedColor.textOnPrimary }}></i>
          </div>
          اللون الرئيسي
        </h3>
        <p className="text-xs text-gray-400 mb-4">يؤثر على الأزرار، الأسعار، والعناصر المميزة في الموقع</p>
        <div className="grid grid-cols-5 gap-3 mb-5">
          {COLOR_PRESETS.map(preset => (
            <button
              key={preset.id}
              onClick={() => { updateDesign({ colorPresetId: preset.id }); applyColorPreset(preset); }}
              title={preset.name}
              className="flex flex-col items-center gap-1.5 cursor-pointer"
            >
              <div
                className="w-full aspect-square rounded-xl border-2 transition-all duration-200 relative"
                style={{
                  background: `linear-gradient(135deg, ${preset.primaryLight} 0%, ${preset.primary} 50%, ${preset.primaryDark} 100%)`,
                  borderColor: design.colorPresetId === preset.id ? preset.primary : 'transparent',
                  boxShadow: design.colorPresetId === preset.id ? `0 0 0 3px ${preset.primary}40` : '0 2px 6px rgba(0,0,0,0.08)',
                  transform: design.colorPresetId === preset.id ? 'scale(1.1)' : 'scale(1)',
                }}
              >
                {design.colorPresetId === preset.id && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-xl">
                    <i className="ri-check-line text-sm font-black" style={{ color: preset.textOnPrimary }}></i>
                  </div>
                )}
              </div>
              <span className="text-[10px] text-gray-500 font-medium whitespace-nowrap">{preset.name}</span>
            </button>
          ))}
        </div>

        {/* Color preview */}
        <div className="bg-[#0f0f0f] rounded-xl p-4 flex items-center justify-between gap-4 mb-4">
          <div className="flex flex-col gap-2">
            <p className="text-gray-400 text-[10px] mb-1">معاينة اللون المختار</p>
            <div className="flex items-center gap-2">
              {[selectedColor.primary, selectedColor.primaryLight, selectedColor.primaryDark].map((c, i) => (
                <div key={i} className="w-8 h-8 rounded-lg flex-shrink-0" style={{ background: c }}></div>
              ))}
            </div>
            <p className="text-gray-500 text-[10px] font-mono" dir="ltr">{selectedColor.primary}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap" style={{ background: selectedColor.primary, color: selectedColor.textOnPrimary }}>
              أضف للسلة
            </div>
            <div className="text-base font-black" style={{ background: `linear-gradient(to left, ${selectedColor.primaryLight}, ${selectedColor.primary})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              ٢٤٩ ر.س
            </div>
          </div>
        </div>

        <SaveButton state={colorSaveState} onClick={onSaveColors} label="حفظ الألوان" />
      </div>

      {/* ── Page Background Color ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-gradient-to-br from-gray-800 to-gray-400">
            <i className="ri-contrast-drop-2-line text-white text-[10px]"></i>
          </div>
          خلفية الصفحة
        </h3>
        <p className="text-xs text-gray-400 mb-4">اختر لون خلفية الصفحة الرئيسية وصفحات المتجر</p>

        {/* Presets grid */}
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-4">
          {PAGE_BG_PRESETS.filter(b => b.id !== 'custom').map(bg => (
            <button
              key={bg.id}
              onClick={() => updateDesign({ pageBgId: bg.id, pageBgCustomColor: bg.color })}
              title={bg.label}
              className="flex flex-col items-center gap-1.5 cursor-pointer group"
            >
              <div
                className="w-full aspect-square rounded-xl border-2 transition-all duration-200 relative overflow-hidden"
                style={{
                  background: bg.color,
                  borderColor: design.pageBgId === bg.id ? selectedColor.primary : 'rgba(0,0,0,0.1)',
                  boxShadow: design.pageBgId === bg.id ? `0 0 0 3px ${selectedColor.primary}40` : '0 2px 6px rgba(0,0,0,0.08)',
                  transform: design.pageBgId === bg.id ? 'scale(1.08)' : 'scale(1)',
                }}
              >
                {design.pageBgId === bg.id && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: selectedColor.primary }}>
                      <i className="ri-check-line text-[8px]" style={{ color: selectedColor.textOnPrimary }}></i>
                    </div>
                  </div>
                )}
                {/* Subtle texture lines */}
                <div className="absolute inset-0 opacity-10" style={{
                  backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.1) 0px, rgba(255,255,255,0.1) 1px, transparent 1px, transparent 8px)',
                }}></div>
              </div>
              <span className="text-[9px] text-gray-500 font-medium text-center leading-tight whitespace-nowrap">{bg.label}</span>
            </button>
          ))}

          {/* Custom color */}
          <button
            onClick={() => updateDesign({ pageBgId: 'custom', pageBgCustomColor: customBg })}
            title="لون مخصص"
            className="flex flex-col items-center gap-1.5 cursor-pointer"
          >
            <div
              className="w-full aspect-square rounded-xl border-2 transition-all duration-200 relative overflow-hidden flex items-center justify-center"
              style={{
                background: `conic-gradient(from 0deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)`,
                borderColor: design.pageBgId === 'custom' ? selectedColor.primary : 'rgba(0,0,0,0.1)',
                boxShadow: design.pageBgId === 'custom' ? `0 0 0 3px ${selectedColor.primary}40` : '0 2px 6px rgba(0,0,0,0.08)',
                transform: design.pageBgId === 'custom' ? 'scale(1.08)' : 'scale(1)',
              }}
            >
              {design.pageBgId === 'custom' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <i className="ri-check-line text-white text-sm font-black"></i>
                </div>
              )}
            </div>
            <span className="text-[9px] text-gray-500 font-medium whitespace-nowrap">مخصص</span>
          </button>
        </div>

        {/* Custom color picker */}
        {design.pageBgId === 'custom' && (
          <div className="flex items-center gap-3 mb-4 p-3 bg-white rounded-xl border border-gray-200">
            <div className="relative">
              <input
                type="color"
                value={customBg}
                onChange={e => {
                  setCustomBg(e.target.value);
                  updateDesign({ pageBgCustomColor: e.target.value });
                }}
                className="w-10 h-10 rounded-lg cursor-pointer border-0 p-0.5 bg-transparent"
                title="اختر لوناً مخصصاً"
              />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold text-[#1a1a1a] mb-0.5">لون مخصص</p>
              <input
                type="text"
                value={customBg}
                onChange={e => {
                  const val = e.target.value;
                  setCustomBg(val);
                  if (/^#[0-9a-fA-F]{6}$/.test(val)) {
                    updateDesign({ pageBgCustomColor: val });
                  }
                }}
                placeholder="#0a0a0a"
                className="text-xs font-mono text-gray-500 bg-transparent outline-none w-full"
                dir="ltr"
                maxLength={7}
              />
            </div>
            <div className="w-8 h-8 rounded-lg border border-gray-200 flex-shrink-0" style={{ background: customBg }}></div>
          </div>
        )}

        {/* Live preview of background */}
        <div
          className="rounded-xl p-4 mb-4 transition-colors duration-300 border"
          style={{
            background: activeBgColor,
            borderColor: 'rgba(128,128,128,0.15)',
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: selectedColor.primary }}>
                <i className="ri-store-2-line text-[10px]" style={{ color: selectedColor.textOnPrimary }}></i>
              </div>
              <span className="text-xs font-bold" style={{ color: isLightBg ? '#1a1a1a' : '#ffffff' }}>متجر إبداع</span>
            </div>
            <div className="flex items-center gap-1.5">
              <i className="ri-search-line text-xs" style={{ color: isLightBg ? '#6b7280' : '#9ca3af' }}></i>
              <i className="ri-shopping-cart-line text-xs" style={{ color: isLightBg ? '#6b7280' : '#9ca3af' }}></i>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { name: 'سماعة لاسلكية', price: '٢٤٩' },
              { name: 'ساعة ذكية', price: '٣٩٩' },
            ].map((p, i) => (
              <div
                key={i}
                className="rounded-lg p-2.5 border"
                style={{
                  background: isLightBg ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.05)',
                  borderColor: isLightBg ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
                }}
              >
                <div className="h-8 rounded-md mb-2" style={{ background: isLightBg ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)' }}></div>
                <p className="text-[10px] font-bold mb-1" style={{ color: isLightBg ? '#1a1a1a' : '#ffffff' }}>{p.name}</p>
                <p className="text-[10px] font-black" style={{ color: selectedColor.primary }}>{p.price} ر.س</p>
              </div>
            ))}
          </div>
          <div className="mt-2 text-center">
            <span className="text-[9px]" style={{ color: isLightBg ? '#9ca3af' : '#4b5563' }}>
              معاينة — {selectedBg.label}{design.pageBgId === 'custom' ? ` (${customBg})` : ''}
            </span>
          </div>
        </div>

        {/* Light bg warning */}
        {isLightBg && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl mb-4">
            <i className="ri-information-line text-amber-500 text-sm flex-shrink-0 mt-0.5"></i>
            <p className="text-xs text-amber-700">
              الخلفية الفاتحة قد تؤثر على مظهر بعض العناصر المصممة للخلفية الداكنة. تأكد من مراجعة الموقع بعد التطبيق.
            </p>
          </div>
        )}

        <SaveButton state={colorSaveState} onClick={onSaveColors} label="حفظ الخلفية" />
      </div>

      {/* ── Gradient Presets ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-gradient-to-br from-pink-400 to-amber-400 rounded-lg">
            <i className="ri-contrast-2-line text-white text-[10px]"></i>
          </div>
          تدرجات جاهزة
        </h3>
        <p className="text-xs text-gray-400 mb-4">تدرجات لونية احترافية للإلهام</p>
        <div className="grid grid-cols-3 gap-3">
          {GRADIENT_PRESETS.map(g => (
            <div
              key={g.name}
              className="h-14 rounded-xl cursor-pointer transition-transform hover:scale-105 flex items-end p-2"
              style={{ background: `linear-gradient(135deg, ${g.from}, ${g.to})` }}
            >
              <span className="text-[10px] font-bold" style={{ color: g.text }}>{g.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Dark Mode ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-[#1a1a1a] rounded-xl">
              <i className="ri-moon-line text-white text-sm"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-[#1a1a1a]">الوضع الليلي</p>
              <p className="text-xs text-gray-400">تفعيل الخلفية الداكنة للموقع</p>
            </div>
          </div>
          <button
            onClick={() => updateDesign({ darkMode: !design.darkMode })}
            className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${design.darkMode ? 'bg-[#1a1a1a]' : 'bg-gray-200'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full shadow transition-all flex items-center justify-center ${design.darkMode ? 'left-6 bg-amber-400' : 'left-0.5 bg-white'}`}>
              {design.darkMode && <i className="ri-moon-fill text-[8px] text-[#1a1a1a]"></i>}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
